/**
 * PlacedSeatsLayer.jsx - Renders placed seats on the Map Editor canvas
 * Elvis Aloha Collection
 * 
 * Renders seats from the placed_seats collection using their templates.
 * Supports selection, hover states, and click interactions in the editor.
 */

import React, { useMemo } from 'react';

const PlacedSeatsLayer = ({
  placedSeats = [],
  templates = [],
  selectedSeatId,
  onSelectSeat,
  transform,
  hoveredSeatId,
  onHoverSeat
}) => {
  // Create a template lookup
  const templateMap = useMemo(() => {
    const map = {};
    templates.forEach(t => {
      map[t.id] = t;
    });
    return map;
  }, [templates]);

  // Don't render until transform is properly calculated by OpenSeadragon
  // The initial default is { scale: 1, translateX: 0, translateY: 0 }
  // A real calculated transform will have non-zero translateX/Y (position of image on screen)
  // since the image is offset from the container edges
  const isTransformReady = transform && (
    transform.translateX !== 0 ||
    transform.translateY !== 0 ||
    (transform.scale > 0 && transform.scale < 1) || // zoomed out
    transform.scale > 1 // zoomed in
  );
  
  if (!isTransformReady || placedSeats.length === 0) {
    console.log('[PlacedSeatsLayer] Waiting for transform:', transform);
    return null;
  }

  return (
    <g className="placed-seats-layer" data-testid="placed-seats-layer">
      {placedSeats.map(seat => {
        const template = templateMap[seat.template_id];
        if (!template) return null;

        const geom = template.geometry || {};
        const width = (geom.width || 17) * (seat.scale_x || 1);
        const height = (geom.height || 14) * (seat.scale_y || 1);
        
        // Calculate screen position from native coords
        // transform has: scale, translateX, translateY
        const screenX = seat.native_x * transform.scale + transform.translateX;
        const screenY = seat.native_y * transform.scale + transform.translateY;
        const screenWidth = width * transform.scale;
        const screenHeight = height * transform.scale;
        
        // Top-left corner (native_x/y is center)
        const tlX = screenX - screenWidth / 2;
        const tlY = screenY - screenHeight / 2;
        
        const isSelected = seat.id === selectedSeatId;
        const isHovered = seat.id === hoveredSeatId;
        
        // Determine fill color based on state
        let fillColor = template.fill_color || 'rgba(201, 169, 98, 0.6)';
        let strokeColor = template.stroke_color || '#C9A962';
        let strokeWidth = template.stroke_width || 1.5;
        
        if (isSelected) {
          fillColor = template.selected_fill || 'rgba(201, 169, 98, 0.85)';
          strokeColor = template.selected_stroke || '#1a365d';
          strokeWidth = 2.5;
        } else if (isHovered) {
          fillColor = template.hover_fill || 'rgba(201, 169, 98, 0.75)';
          strokeWidth = 2;
        }
        
        // Status indicator color
        const statusColor = seat.publication_status === 'published' ? '#4CAF50' : 
                           seat.publication_status === 'approved' ? '#2196F3' : '#888';

        return (
          <g 
            key={seat.id}
            className={`placed-seat ${isSelected ? 'placed-seat--selected' : ''}`}
            transform={`rotate(${seat.rotation || 0}, ${screenX}, ${screenY})`}
            onClick={(e) => {
              e.stopPropagation();
              onSelectSeat?.(seat.id);
            }}
            onMouseEnter={() => onHoverSeat?.(seat.id)}
            onMouseLeave={() => onHoverSeat?.(null)}
            style={{ cursor: 'pointer' }}
            data-testid={`placed-seat-${seat.id}`}
          >
            {/* Seat shape */}
            <rect
              x={tlX}
              y={tlY}
              width={screenWidth}
              height={screenHeight}
              fill={fillColor}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              rx={1}
            />
            
            {/* Status indicator dot */}
            <circle
              cx={tlX + screenWidth - 3}
              cy={tlY + 3}
              r={3}
              fill={statusColor}
            />
            
            {/* Selection handles when selected */}
            {isSelected && (
              <>
                {/* Corner handles */}
                {[
                  { x: tlX, y: tlY },
                  { x: tlX + screenWidth, y: tlY },
                  { x: tlX, y: tlY + screenHeight },
                  { x: tlX + screenWidth, y: tlY + screenHeight }
                ].map((pos, i) => (
                  <rect
                    key={i}
                    x={pos.x - 4}
                    y={pos.y - 4}
                    width={8}
                    height={8}
                    fill="#fff"
                    stroke="#1a365d"
                    strokeWidth={1}
                  />
                ))}
                
                {/* Rotation handle */}
                <line
                  x1={screenX}
                  y1={tlY}
                  x2={screenX}
                  y2={tlY - 20}
                  stroke="#1a365d"
                  strokeWidth={1}
                />
                <circle
                  cx={screenX}
                  cy={tlY - 20}
                  r={5}
                  fill="#C9A962"
                  stroke="#1a365d"
                  strokeWidth={1}
                  style={{ cursor: 'grab' }}
                />
              </>
            )}
            
            {/* Label on hover/select */}
            {(isSelected || isHovered) && (
              <text
                x={screenX}
                y={tlY - 5}
                textAnchor="middle"
                fontSize={10}
                fill="#C9A962"
                fontWeight="bold"
              >
                {seat.section} R{seat.row} S{seat.seat}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
};

/**
 * GhostPreviewCursor - Shows template preview following cursor during placement
 */
export const GhostPreviewCursor = ({
  template,
  transform,
  cursorPosition, // { x, y } in screen coordinates
  rotation = 0,
  visible = false
}) => {
  if (!visible || !template || !transform || !cursorPosition) return null;

  const geom = template.geometry || {};
  const width = (geom.width || 17) * transform.scale;
  const height = (geom.height || 14) * transform.scale;
  
  // Position centered at cursor
  const x = cursorPosition.x - width / 2;
  const y = cursorPosition.y - height / 2;

  return (
    <g 
      className="ghost-preview-cursor"
      transform={`rotate(${rotation}, ${cursorPosition.x}, ${cursorPosition.y})`}
      style={{ pointerEvents: 'none' }}
      data-testid="ghost-preview-cursor"
    >
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill="rgba(201, 169, 98, 0.4)"
        stroke="#C9A962"
        strokeWidth={2}
        strokeDasharray="4 2"
        rx={1}
      />
      <circle
        cx={cursorPosition.x}
        cy={cursorPosition.y}
        r={3}
        fill="#C9A962"
      />
    </g>
  );
};

export default PlacedSeatsLayer;
